import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5 } from "styled-components";
import { m } from "framer-motion";
import React from "react";
import { type Mode, type Theme, getThemeStyle } from "~/features/ui/get-theme-style";
type Props = {
  tableContainerRef: React.RefObject<HTMLDivElement | null>;
  tableRef: React.RefObject<HTMLDivElement | null>;
  floatingProductActionsRef: React.RefObject<HTMLDivElement | null>;
  isOverflowing: boolean;
  mode: Mode;
  theme: Theme;
};
export function HardwareComparisonTableScrollbar({
  tableContainerRef,
  tableRef,
  floatingProductActionsRef,
  isOverflowing,
  mode,
  theme
}: Props) {
  const themeStyle = getThemeStyle({
    mode,
    theme
  });
  const scrollbarRef = React.useRef<HTMLDivElement>(null);
  const [scrollbarWidth, setScrollbarWidth] = React.useState(0);
  const [scrollbarX, setScrollbarX] = React.useState(0);
  const updateScrollbar = React.useCallback(() => {
    if (!tableContainerRef.current || !tableRef.current || !scrollbarRef.current) return;
    const containerElement = tableContainerRef.current;
    const scrollbarElement = scrollbarRef.current;
    const {
      scrollLeft,
      clientWidth
    } = containerElement;
    const {
      scrollWidth
    } = tableRef.current;
    const scrollableWidth = scrollWidth - clientWidth;
    if (floatingProductActionsRef.current) {
      floatingProductActionsRef.current.scrollLeft = scrollLeft;
    }
    const newScrollbarWidth = Math.max(50, clientWidth / scrollWidth * scrollbarElement.clientWidth);
    const scrollPercentage = scrollLeft / scrollableWidth;
    const maxScrollbarX = scrollbarElement.clientWidth - newScrollbarWidth;
    const newScrollbarX = scrollPercentage * maxScrollbarX;
    setScrollbarWidth(newScrollbarWidth);
    setScrollbarX(newScrollbarX);
  }, []);
  const handleScrollbarDrag = React.useCallback((event: MouseEvent | TouchEvent | PointerEvent, info: {
    point: {
      x: number;
    };
    delta: {
      x: number;
    };
  }) => {
    if (!tableContainerRef.current || !tableRef.current || !scrollbarRef.current) return;
    const scrollbarRect = scrollbarRef.current.getBoundingClientRect();
    const containerElement = tableContainerRef.current;
    const {
      clientWidth
    } = containerElement;
    const {
      scrollWidth
    } = tableRef.current;
    const scrollableWidth = scrollWidth - clientWidth;
    const scrollbarTrackWidth = scrollbarRect.width - scrollbarWidth;
    const maxScrollbarX = scrollbarTrackWidth;
    const newScrollbarX = Math.max(0, Math.min(scrollbarX + info.delta.x, maxScrollbarX));
    const scrollPercentage = newScrollbarX / scrollbarTrackWidth;
    containerElement.scrollLeft = scrollPercentage * scrollableWidth;
    setScrollbarX(newScrollbarX);
  }, [scrollbarWidth, scrollbarX]);
  React.useEffect(() => {
    updateScrollbar();
    tableContainerRef.current?.addEventListener("scroll", updateScrollbar);
    window.addEventListener("resize", updateScrollbar);
    return () => {
      tableContainerRef.current?.removeEventListener("scroll", updateScrollbar);
      window.removeEventListener("resize", updateScrollbar);
    };
  }, [tableContainerRef, updateScrollbar]);
  if (!isOverflowing) return null;
  return <_StyledDiv className="scrollbar" $_css={[{
    "width": "100%"
  }, themeStyle.background, themeStyle.border]}>
      <_StyledDiv2>
        <_StyledDiv3 ref={scrollbarRef}>
          {/* Invisible larger hitbox to make it easier to drag */}
          <_StyledMDiv drag="x" dragConstraints={scrollbarRef} dragElastic={0} dragMomentum={false} onDrag={handleScrollbarDrag} style={{
          width: scrollbarWidth,
          x: scrollbarX
        }} />
          {/* Visible scrollbar */}
          <_StyledMDiv2 style={{
          width: scrollbarWidth,
          x: scrollbarX
        }} />
        </_StyledDiv3>
      </_StyledDiv2>
    </_StyledDiv>;
}
var _StyledDiv = /*#__PURE__*/_styled("div").withConfig({
  componentId: "sc-sf-xq04n3-0"
})`${p => p.$_css}`;
var _StyledDiv2 = _styled2("div").withConfig({
  componentId: "sc-sf-xq04n3-1"
})({
  "backgroundColor": "rgb(75 46 248 / 0.2)"
});
var _StyledDiv3 = _styled3("div").withConfig({
  componentId: "sc-sf-xq04n3-2"
})({
  "position": "relative",
  "height": "0.4rem",
  "width": "100%"
});
var _StyledMDiv = _styled4(m.div).withConfig({
  componentId: "sc-sf-xq04n3-3"
})({
  "position": "absolute",
  "top": "-1rem",
  "height": "2.4rem",
  "cursor": "pointer"
});
var _StyledMDiv2 = /*#__PURE__*/_styled5(m.div).withConfig({
  displayName: "scrollbar___StyledMDiv2",
  componentId: "sc-s64caj-0"
})({
  "pointerEvents": "none",
  "position": "absolute",
  "top": "0",
  "height": "100%",
  "--tw-bg-opacity": "1",
  "backgroundColor": "rgb(75 46 248 / var(--tw-bg-opacity))"
});